// Point d'entrée de l'interface. Un routeur minuscule, un bandeau, et des écrans
// qui ne savent rien les uns des autres. Toute action passe par un seul aiguillage.
import { api, direRefus, fichier, Refus } from './api.ts';
import { $, brut, h, mot, peindre, surClic, valeur, type Brut } from './ui.ts';
import { estAdmin, peutEcrire, type Referentiel, type Session } from './etat.ts';
import { ecranAcces, ecranConnexion } from './ecrans/connexion.ts';
import { ecranAccueil } from './ecrans/accueil.ts';
import { basculerFiltre, basculerVolet, ecranVivier, etatFiltres, viderFiltres,
         type Facette } from './ecrans/vivier.ts';
import { couchDocument, ecranProfil, remplirTexteDocument } from './ecrans/profil.ts';
import { ouvrirExtraction, ouvrirPrefill } from './ecrans/extraction.ts';
import { ecranMission, ecranMissions, etatFiltresMissions } from './ecrans/missions.ts';
import { ouvrirBrief } from './ecrans/brief.ts';
import { FICHE_VIDE, brancherFiche, coucheFiche, recolterProfil, type FicheProfil } from './ecrans/fiche.ts';
import { ouvrirSynthese } from './ecrans/synthese.ts';
import { ecranNotice } from './ecrans/notice.ts';
import { ecranConsole, enregistrerParametre } from './ecrans/console.ts';

type Vue = 'accueil' | 'vivier' | 'profil' | 'missions' | 'mission' | 'console';

let session: Session | null = null;
let referentiel: Referentiel = { roles: [], familles: {} };
let vue: Vue = 'vivier';
let profilOuvert: string | null = null;
let missionOuverte: string | null = null;
let periode = 'origine';

const ONGLETS: [Vue, string, boolean][] = [
  ['accueil', 'Accueil', true], ['vivier', 'Vivier', false],
  ['missions', 'Missions', false], ['console', 'Console', true],
];

const chrome = (corps: Brut): Brut => h`
  <header class="top">
    <div class="marque"><b>MANA</b><i></i><s>WAKEA</s></div>
    <nav class="nav">${ONGLETS.filter(([, , admin]) => !admin || estAdmin(session!))
      .map(([v, libelle]) => h`<button data-act="aller" data-vue="${v}"
        ${vue === v ? brut('aria-current="page"') : ''}>${libelle}</button>`)}</nav>
    <span class="pousse"></span>
    <details class="menu" id="menu-qui">
      <summary class="qui" title="${session!.prenom} ${session!.nom}">${session!.trigramme}</summary>
      <div class="menu-liste">
        <div class="menu-qui">${session!.prenom} ${session!.nom}<span class="pt sourd">${session!.role}</span></div>
        <button data-act="aide">Aide</button>
        <button data-act="deconnexion">Se déconnecter</button>
      </div></details>
  </header>
  ${corps}`;

function couche(contenu: Brut | null): void {
  peindre('#couche', contenu ?? h``);
}

/**
 * La clé d'idempotence de la fiche en cours de saisie. Tirée à l'ouverture de la couche et
 * non au moment de l'envoi : c'est ce qui fait que deux envois successifs portent la même,
 * et décrivent donc au serveur la même intention de créer.
 */
let cleCreationProfil = '';

/** Les couches qui portent une fiche profil ont un équivalent journalier à tenir à jour. */
function coucheDeFiche(titre: string, sur: string, f: FicheProfil, forme: string,
                       action: string, avecOrigine: boolean): void {
  couche(coucheFiche(titre, sur, f, referentiel, forme, action, avecOrigine));
  brancherFiche();
}

// ─── Couches ────────────────────────────────────────────────────────────────

const coucheInvitation = (): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche" style="max-width:560px">
    <div class="ct"><div><div class="sur">COMPTES</div><h2>Inviter une personne</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <form class="cc" id="inviter">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        <div><label class="lab" for="i-prenom">PRÉNOM</label><input class="champ" id="i-prenom" required></div>
        <div><label class="lab" for="i-nom">NOM</label><input class="champ" id="i-nom" required></div></div>
      <div><label class="lab" for="i-email">ADRESSE PROFESSIONNELLE</label>
        <input class="champ" id="i-email" type="email" required placeholder="prenom.nom@wakea-partners.com"></div>
      <div><label class="lab" for="i-role">RÔLE</label>
        <select class="champ" id="i-role">
          <option value="lecture">Lecture — consulte, ne télécharge pas</option>
          <option value="contributeur" selected>Contributeur — crée, qualifie, dépose</option>
          <option value="administrateur">Administrateur — comptes, seuils, conformité</option></select></div>
      <div class="enc ar"><span class="sur">CE QUI SE PASSE ENSUITE</span>
        <p>Un lien valable sept jours part vers cette adresse. La personne y choisit son mot de passe
           et appaire son application d'authentification. Tant qu'elle ne l'a pas fait, le compte n'ouvre rien.</p></div>
      <p class="err" id="err" hidden></p>
    </form>
    <div class="cp"><button class="bt" data-act="fermer">Annuler</button>
      <button class="bt p" form="inviter" type="submit">Envoyer l'invitation</button></div>
  </div></div>`;

/**
 * L'aide. Un seul écran, lu une fois : ce que chaque vue sert à faire, ce que chaque geste
 * produit, et ce que MANA refuse. Ce dernier point n'est pas une formalité — la moitié des
 * questions qu'on se pose devant un outil sont « pourquoi ça ne me laisse pas faire ça ».
 */
const coucheAide = (): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche large-c">
    <div class="ct"><div><div class="sur">MANA · AIDE</div><h2>Comment ça marche</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <div class="cc aide-txt">
      <section>
        <h3>Les quatre écrans</h3>
        <p><b>Usage</b> — ce que le cabinet a fait, et ce que vaut le vivier. Les répartitions
          sont cliquables : chaque barre ouvre le vivier sur cette case.</p>
        <p><b>Vivier</b> — les personnes. La recherche porte sur tout le dossier ; les facettes
          à gauche se cochent à plusieurs et se cumulent entre elles.</p>
        <p><b>Missions</b> — les besoins clients, leur brief relu, et les candidats rattachés.</p>
        <p><b>Console</b> — réservée aux administrateurs : comptes, seuils, journal, coûts.</p>
      </section>
      <section>
        <h3>Faire entrer quelqu'un au vivier</h3>
        <p>Trois chemins. <b>Saisir à la main</b> pour une personne rencontrée.
          <b>À partir d'un CV</b> : le modèle lit le document et propose une fiche, que vous
          relisez avant qu'elle n'existe. Et les <b>candidatures du site</b>, qui arrivent seules.</p>
        <p>Une candidature venue du site porte l'étiquette <b>Entrée du site</b> et n'appartient
          à personne. <b>La prendre en main</b>, depuis sa fiche, la met à votre nom : elle quitte
          la file des entrées, et quelqu'un répond désormais d'elle. Tant que personne ne l'a
          prise, le dossier dort — ce qui est un gâchis, et un défaut de conformité, puisque
          la personne a été informée que nous allions la regarder.</p>
      </section>
      <section>
        <h3>Qualifier un dossier</h3>
        <p>La <b>synthèse</b> est produite à partir de la fiche, des expériences, des notes et du
          compte rendu d'entretien. Le modèle propose, vous relisez, et rien ne s'écrit sans vous.</p>
        <p>Le passage à l'état <b>Qualifié</b> exige un compte rendu ou une retranscription
          d'entretien au dossier. Sans entretien, on ne présente personne.</p>
        <p>La <b>cartographie IA</b> — la chaîne, les terrains, la profondeur, les postures — se
          règle au clic sur la fiche. Une case vide veut dire « non constaté », jamais « faible ».</p>
      </section>
      <section>
        <h3>Les missions</h3>
        <p>Une mission part de ce que le client a écrit : <b>collez son courriel, ses notes, son
          message</b> — ou déposez sa fiche de poste. N'arrangez rien : ce qui est mal dit dans la
          demande doit rester mal dit, c'est ce que le modèle signalera comme question à poser.</p>
        <p>Le modèle en tire un <b>brief normalisé</b> : l'enjeu, les compétences requises et
          souhaitables, les livrables, les critères de succès, la posture IA attendue, et la liste
          de <b>ce qu'il reste à demander au client</b>. Vous relisez, vous corrigez, et la mission
          n'existe qu'à ce moment-là. Chaque mission reçoit une référence lisible — M-2026-001.</p>
        <p>Depuis sa fiche : <b>produire la fiche de mission en PDF</b>, au modèle du cabinet, telle
          qu'elle partira au candidat. Le client y est <b>anonymisé</b> — secteur, taille et région,
          jamais le nom ni la ville. Les pièces envoyées par le client se déposent au même endroit.</p>
        <p>Les <b>candidats</b> rattachés portent leur provenance : ajoutés à la main, ou proposés
          par le moteur de rapprochement quand il existera. Un candidat écarté exige un motif —
          c'est la seule donnée qui dise où l'évaluation s'est trompée. Une mission perdue aussi.</p>
        <p>Une mission créée par erreur <b>s'efface</b>, mais seulement par un administrateur, avec
          un motif, et jamais si des candidatures y sont rattachées : les effacer emporterait leur
          évaluation.</p>
      </section>
      <section>
        <h3>Le CV WAKEA et le dossier de présentation</h3>
        <p>Deux documents sortent d'une fiche profil, depuis la section <b>LE CV WAKEA</b> de la
          colonne de droite.</p>
        <p>Le <b>CV WAKEA (PDF)</b> est le document à la charte du cabinet : identité, repères,
          synthèse, points forts, cartographie IA, parcours, formation, langues. Il remplace
          l'envoi du CV source, qui reste un actif interne. Pour le composer il faut une synthèse,
          au moins une expérience, et un titre ou un rôle de la grille — le bouton dit ce qui
          manque tant qu'il manque quelque chose.</p>
        <p>Le <b>dossier de présentation (JSON)</b> porte la même matière sous forme de données.
          C'est le fichier à déposer dans la conversation de l'agent de propositions commerciales :
          il n'a alors plus rien à deviner.</p>
        <p>Ni l'un ni l'autre ne portent de <b>coordonnées</b>, de <b>tarif</b>, de
          <b>disponibilité</b> ni de <b>statut contractuel</b>. Les points de vigilance et les
          questions ouvertes de la synthèse n'en sortent pas non plus : ils sont écrits pour
          décider en comité, pas pour être lus par celui qu'on démarche. Les deux documents sont
          recomposés à chaque demande — rien n'est stocké, donc rien ne se périme en silence.</p>
      </section>
      <section>
        <h3>La proposition commerciale</h3>
        <p>Elle se construit aujourd'hui <b>hors de MANA</b> : vous sortez le dossier de présentation
          de chaque profil pressenti, vous le déposez dans la conversation de l'agent de
          propositions avec la fiche de mission, et l'agent produit le PowerPoint à la charte —
          couverture, contexte, points d'attention, réponse, trajectoire, gouvernance, une fiche
          par profil, la synthèse comparative, et les CV WAKEA en PDF.</p>
        <p>La <b>fiche profil</b> d'une proposition n'est pas le CV : elle porte les points
          d'attention de cette mission-là, les niveaux retenus, le tarif de vente et le « pourquoi
          ce profil ». Rien de cela n'existe hors d'une mission, et c'est pourquoi elle ne se
          produit pas depuis une fiche seule.</p>
        <p>La génération de la proposition <b>depuis MANA</b>, à partir d'une mission enregistrée,
          est le prochain chantier. Le moteur de rendu est déjà en place — c'est lui qui compose
          les CV WAKEA.</p>
      </section>
      <section>
        <h3>Ce qui est compté</h3>
        <p>Chaque sortie — CV WAKEA, dossier de présentation, téléchargement d'un document du
          dossier — est inscrite au journal et comptée dans un <b>seuil mensuel</b> par personne,
          réglé par un administrateur dans la console. Le seuil ne porte pas sur un format de
          fichier : il porte sur le fait que de la matière concernant quelqu'un quitte MANA.</p>
        <p>Il n'existe pas d'export en masse, ni de téléchargement en lot. Les CV sont un actif
          du cabinet.</p>
      </section>
      <section>
        <h3>Les trois trigrammes qui ne sont pas des gens</h3>
        <p>Le journal n'accepte pas de ligne anonyme : toute action porte un trigramme. Trois
          d'entre eux ne désignent personne au cabinet. <b>SITE</b> — une candidature arrivée par
          le site. <b>PER</b> — la personne concernée elle-même, quand elle accuse réception de la
          notice ou s'oppose à être conservée. <b>SYS</b> — la création du tout premier compte
          administrateur, à la naissance de la base.</p>
      </section>
      <section>
        <h3>Ce que MANA refuse</h3>
        <ul>
          <li>Aucune décision automatique : le modèle propose, un humain tranche et signe.</li>
          <li>Pas d'export global du vivier, ni de téléchargement en lot.</li>
          <li>Pas de réenregistrement d'une personne qui s'est opposée à être conservée.</li>
          <li>Le journal est scellé : une ligne ne se modifie ni ne s'efface.</li>
          <li>Effacer une mission est réservé aux administrateurs, avec motif, et refusé dès
            qu'une candidature y est rattachée.</li>
        </ul>
      </section>
      <section>
        <h3>Une question, un défaut ?</h3>
        <p>Écrivez à <b>contact@wakea-partners.com</b>. Si c'est un message d'erreur, notez la
          référence qu'il affiche : elle permet de retrouver la trace exacte.</p>
      </section>
    </div>
    <div class="cp"><button class="bt p" data-act="fermer">J'ai compris</button></div>
  </div></div>`;

const coucheMotif = (titre: string, phrase: string, action: string, id?: string): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche" style="max-width:520px">
    <div class="ct"><div><div class="sur">GESTE JOURNALISÉ</div><h2>${titre}</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <div class="cc"><div class="enc ar"><span class="sur">LE MOTIF EST OBLIGATOIRE</span><p>${phrase}</p></div>
      <div><label class="lab" for="motif">MOTIF</label>
        <textarea class="champ" id="motif" rows="3" minlength="10" maxlength="500"></textarea></div>
      <p class="err" id="err" hidden></p></div>
    <div class="cp"><button class="bt" data-act="fermer">Annuler</button>
      <button class="bt p" data-act="${action}" ${id ? brut(`data-id="${id}"`) : ''}>Confirmer</button></div>
  </div></div>`;

// ─── Routage ────────────────────────────────────────────────────────────────

async function rendre(): Promise<void> {
  if (!session) return;
  try {
    if (vue === 'accueil' && estAdmin(session)) await ecranAccueil(periode, chrome);
    else if (vue === 'console' && estAdmin(session)) await ecranConsole(chrome);
    else if (vue === 'profil' && profilOuvert) await ecranProfil(profilOuvert, session, chrome);
    else if (vue === 'missions') await ecranMissions(session, chrome);
    else if (vue === 'mission' && missionOuverte) await ecranMission(missionOuverte, session, chrome);
    else { vue = 'vivier'; await ecranVivier(session, referentiel, chrome); }
    location.hash = vue === 'profil' ? `#/profil/${profilOuvert}`
      : vue === 'mission' ? `#/mission/${missionOuverte}` : `#/${vue}`;
  } catch (e) {
    // Session expirée en cours d'usage : on revient à la connexion, sans recharger.
    if (e instanceof Refus && e.statut === 401) { session = null; return ecranConnexion(() => void demarrer()); }
    mot(direRefus(e));
  }
}

function lireAdresse(): void {
  const [, nom, id] = location.hash.replace(/^#\//, '').split('/');
  const demandee = (location.hash.replace(/^#\//, '').split('/')[0] ?? '') as Vue;
  if (demandee === 'profil') { vue = 'profil'; profilOuvert = nom ?? id ?? null; }
  else if (demandee === 'mission') { vue = 'mission'; missionOuverte = nom ?? id ?? null; }
  else if (['accueil', 'vivier', 'missions', 'console'].includes(demandee)) vue = demandee;
  else vue = session && estAdmin(session) ? 'accueil' : 'vivier';
}

async function demarrer(): Promise<void> {
  // La page de notice ne relève pas de l'application : elle s'adresse à quelqu'un qui
  // n'a pas de compte, et ne doit surtout pas passer par l'écran de connexion.
  if (location.pathname === '/notice') {
    const jeton = new URLSearchParams(location.search).get('jeton');
    if (jeton) return ecranNotice(jeton);
  }
  if (location.pathname === '/acces') {
    const jeton = new URLSearchParams(location.search).get('jeton');
    if (jeton) return ecranAcces(jeton, () => void demarrer());
  }
  try {
    session = await api.get<Session>('/api/moi');
    referentiel = await api.get<Referentiel>('/api/referentiel');
    lireAdresse();
    await rendre();
  } catch {
    session = null;
    ecranConnexion(() => void demarrer());
  }
}

window.addEventListener('hashchange', () => { if (session) { lireAdresse(); void rendre(); } });

// ─── Aiguillage des gestes ──────────────────────────────────────────────────

const dire = (message: string) => { const n = $('#err'); if (n) { n.textContent = message; n.hidden = false; } };

/**
 * Le menu du trigramme se referme.
 *
 * `<details>` ne se ferme pas tout seul quand on clique ailleurs — c'est un repli, pas un
 * menu. Un menu qui reste ouvert derrière l'écran suivant est un menu qu'on a l'air d'avoir
 * cassé, d'où ces deux lignes. Le clic extérieur et la touche d'échappement le ferment,
 * comme partout ailleurs.
 */
const fermerMenu = () => { ($('#menu-qui') as HTMLDetailsElement | null)?.removeAttribute('open'); };

/**
 * Où le geste a commencé.
 *
 * Un clic sur le voile ferme la couche. Mais le navigateur émet aussi un `click` sur le
 * voile quand une sélection de texte **commencée dans un champ** se relâche au-dessus de
 * lui : il suffit de glisser trois pixels au-delà du bord en sélectionnant une ligne pour
 * que la couche se referme et que toute la saisie disparaisse. On ne ferme donc que si le
 * geste a commencé ET fini sur le voile.
 */
let departGeste: EventTarget | null = null;
document.addEventListener('mousedown', (ev) => { departGeste = ev.target; }, true);
document.addEventListener('click', (ev) => {
  const menu = $('#menu-qui') as HTMLDetailsElement | null;
  if (menu?.open && !menu.contains(ev.target as Node)) fermerMenu();
});
document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') fermerMenu(); });

surClic(async (action, cible, ev) => {
  const id = cible.dataset['id'];
  switch (action) {
    case 'aller': vue = cible.dataset['vue'] as Vue; profilOuvert = null; missionOuverte = null; await rendre(); break;
    case 'profil': profilOuvert = id!; vue = 'profil'; await rendre(); break;
    case 'mission': missionOuverte = id!; vue = 'mission'; await rendre(); break;
    case 'periode': periode = cible.dataset['valeur']!; await rendre(); break;

    // Un clic pose la valeur, le même clic la retire. Toutes les facettes se cochent
    // désormais à plusieurs : il n'y a plus deux gestes à distinguer, donc plus qu'un cas.
    case 'filtre':
      basculerFiltre(cible.dataset['cle'] as Facette, cible.dataset['valeur']!);
      await rendre(); break;

    // Retirer le texte de recherche sans toucher aux cases cochées.
    case 'retirer-q': etatFiltres.q = ''; etatFiltres.page = 1; await rendre(); break;

    case 'fraiche': etatFiltres.fraiche = !etatFiltres.fraiche; await rendre(); break;
    case 'entrees': etatFiltres.entrees = !etatFiltres.entrees; etatFiltres.page = 1; await rendre(); break;
    case 'prefiller': etatFiltres.prefiller = !etatFiltres.prefiller; etatFiltres.page = 1; await rendre(); break;
    case 'resynthese': etatFiltres.resynthese = !etatFiltres.resynthese; etatFiltres.page = 1; await rendre(); break;
    // Depuis l'accueil : ouvrir le vivier déjà réduit à ce qui attend d'être pris en main.
    case 'voir-entrees':
      viderFiltres(); etatFiltres.entrees = true;
      vue = 'vivier'; profilOuvert = null; missionOuverte = null; await rendre(); break;

    case 'voir-resynthese':
      viderFiltres(); etatFiltres.resynthese = true;
      vue = 'vivier'; profilOuvert = null; missionOuverte = null; await rendre(); break;

    // Depuis la carte IA de l'accueil : ouvrir le vivier sur cette case de la carte.
    // Les cinq axes mènent au même endroit — seule change la case où l'on dépose la
    // valeur. Cinq `case` séparés auraient répété quatre fois la même remise à zéro, et
    // c'est précisément le genre de répétition où l'on oublie un champ au sixième axe.
    case 'voir-chaine': case 'voir-terrain': case 'voir-famille': case 'voir-role':
    case 'voir-profondeur': case 'voir-posture': {
      const ou = { 'voir-chaine': 'chaine', 'voir-terrain': 'terrain', 'voir-famille': 'famille',
                   'voir-role': 'role', 'voir-profondeur': 'niveau', 'voir-posture': 'ia' } as const;
      // Toutes les facettes portent une liste : la case ouvre le vivier sur la sienne, et
      // le reste est remis à zéro — on vient de l'accueil pour voir *cette* case.
      viderFiltres();
      basculerFiltre(ou[action as keyof typeof ou] as Facette, cible.dataset['valeur']!);
      vue = 'vivier'; profilOuvert = null; missionOuverte = null; await rendre(); break;
    }

    case 'voir-a-lire':
      viderFiltres(); etatFiltres.prefiller = true;
      vue = 'vivier'; profilOuvert = null; missionOuverte = null; await rendre(); break;
    case 'volet': basculerVolet(); await rendre(); break;
    case 'filtre-m': {
      const v = cible.dataset['valeur']!;
      etatFiltresMissions.statut = etatFiltresMissions.statut === v ? '' : v;
      etatFiltresMissions.page = 1; await rendre(); break;
    }
    // Vider : une seule fonction le fait, au même endroit que l'état. Recopier la liste
    // des filtres ici était le moyen sûr d'en oublier un au prochain ajout — c'est ce qui
    // est arrivé à `niveau`, resté sur une chaîne vide quand les autres passaient en liste.
    case 'raz': viderFiltres(); await rendre(); break;

    // Lire le CV déjà au dossier. Déclenché à la main, et seulement à la main : c'est la
    // garantie d'un humain dans la boucle que veut le règlement européen sur l'IA.
    case 'prefill':
      void ouvrirPrefill(referentiel, id!, cible.dataset['doc']!, cible.dataset['nom'] ?? 'CV',
                    async () => { couche(null); await rendre(); });
      break;

    // Prendre un dossier en main : il quitte la file des entrées et porte désormais un nom.
    // C'est le geste que le cahier des charges attend après une candidature spontanée.
    case 'prendre':
      try {
        await api.patch(`/api/profils/${id}`, { proprietaire: session!.trigramme });
        mot('Dossier pris en main — il porte votre trigramme.');
        await rendre();
      } catch (e) { dire(direRefus(e)); }
      break;

    case 'fermer': couche(null); break;
    case 'fermer-voile': if (ev.target === cible && departGeste === cible) couche(null); break;

    case 'nouveau':
      cleCreationProfil = crypto.randomUUID();
      coucheDeFiche('Créer une fiche', 'NOUVEAU PROFIL', { ...FICHE_VIDE, origine: 'saisie' },
                    'creer', 'Créer la fiche', true);
      break;

    case 'modifier':
      try {
        const { profil } = await api.get<{ profil: FicheProfil & { prenom: string; nom: string } }>(`/api/profils/${id}`);
        coucheDeFiche(`${profil.prenom} ${profil.nom}`, 'MODIFIER LA FICHE',
                      { ...FICHE_VIDE, ...profil }, 'modifier', 'Enregistrer', true);
      } catch (e) { mot(direRefus(e)); }
      break;

    case 'notice':
      try {
        const { notice } = await api.post<{ notice: string }>(`/api/profils/${id}/notice`);
        mot(notice === 'envoyee' ? 'Notice envoyée et journalisée.'
          : notice === 'sans_adresse' ? "Aucune adresse au dossier : la notice n'a pas pu partir."
          : "La notice n'est pas partie. L'échec est inscrit au journal.");
        await rendre();
      } catch (e) { mot(direRefus(e)); }
      break;

    case 'synthese':
      ouvrirSynthese(id!, () => { couche(null); void rendre(); });
      break;
    case 'nouvelle-mission':
      ouvrirBrief(referentiel, (creee) => {
        couche(null); missionOuverte = creee; vue = 'mission'; void rendre();
      });
      break;

    case 'statut-m': {
      const statut = valeur('#statut-m');
      if (statut === 'perdue') {
        couche(coucheMotif('Mission perdue',
          `Le motif dit ce qu'il aurait fallu faire autrement. C'est la seule donnée qui serve
           à corriger, et elle ne s'écrit jamais après coup.`, 'perdue!', id));
      } else {
        try { await api.post(`/api/missions/${id}/statut`, { statut }); mot('État changé.'); await rendre(); }
        catch (e) { mot(direRefus(e)); }
      }
      break;
    }
    case 'perdue!':
      try {
        await api.post(`/api/missions/${id}/statut`, { statut: 'perdue', motif: valeur('#motif') });
        couche(null); mot('Mission close, motif inscrit au journal.'); await rendre();
      } catch (e) { dire(direRefus(e)); }
      break;

    case 'cand':
      try {
        await api.patch(`/api/candidatures/${id}`, {
          statut: valeur(`#st-${id}`), motif: valeur(`#mo-${id}`) || undefined,
        });
        mot('Décision enregistrée.'); await rendre();
      } catch (e) { mot(direRefus(e)); }
      break;

    case 'rattacher':
      try {
        await api.post(`/api/missions/${cible.dataset['mission']}/candidatures`, { profil_id: id });
        mot('Profil rattaché.'); await rendre();
      } catch (e) { mot(direRefus(e)); }
      break;
    case 'lire-cv':
      ouvrirExtraction(referentiel, (cree) => {
        couche(null); profilOuvert = cree; vue = 'profil'; void rendre();
      });
      break;
    case 'inviter': couche(coucheInvitation()); break;

    case 'voir': {
      const nom = cible.dataset['nom'] ?? 'Document';
      couche(couchDocument(id!, nom));
      // Le texte arrive après la couche : l'ouverture ne doit pas attendre la lecture.
      void remplirTexteDocument(id!);
      break;
    }
    case 'telecharger': location.href = `/api/documents/${id}/contenu?mode=telecharger`; break;

    // Le dossier de présentation se compose à la demande ; le CV WAKEA, lui, est conservé
    // et n'est refait que si la fiche a bougé. Dans les deux cas l'attente peut durer
    // quelques secondes, et le bouton le dit pendant ce temps — sans quoi on clique trois
    // fois et on sort trois fois le même document.
    //
    // `data-mode` vient de l'écran, qui sait déjà si le fichier conservé est bon : il
    // n'est pas là pour décider — le serveur décide — mais pour que l'attente s'annonce
    // juste. « Composition… » devant un téléchargement d'une demi-seconde ferait croire
    // que le moteur a tourné, et donc que le cabinet a repayé un document qu'il avait déjà.
    case 'cv-wakea':
    case 'dossier': {
      const bouton = cible as HTMLButtonElement;
      const libelle = bouton.textContent;
      const mode = bouton.dataset['mode'] ?? 'produire';
      bouton.disabled = true;
      bouton.textContent = action === 'dossier' ? 'Export…'
        : mode === 'reprise' ? 'Téléchargement…' : 'Composition…';
      try {
        const nom = await fichier(`/api/profils/${id}/${action === 'cv-wakea' ? 'cv-wakea' : 'dossier'}`);
        mot(`${nom} — inscrit au journal.`);
        // Un CV produit ou refait vient de se ranger au dossier : la fiche le dit mal
        // tant qu'elle n'est pas relue — mauvaise date, mauvais libellé de bouton, et le
        // document absent de la liste. Une reprise, elle, n'a rien changé.
        if (action === 'cv-wakea' && mode !== 'reprise') { await rendre(); return; }
      } catch (e) { dire(direRefus(e)); }
      finally { bouton.disabled = false; bouton.textContent = libelle; }
      break;
    }

    // Effacer une mission créée par erreur. Deux temps : la confirmation demande le motif,
    // et c'est seulement le second geste qui efface.
    case 'effacer-m':
      couche(coucheMotif('Effacer la mission',
        `« ${cible.dataset['nom'] ?? 'Cette mission'} » sera effacée définitivement, avec son brief et
         ses documents. Le journal garde la trace du geste et de sa raison. Une mission qui porte
         des candidatures ne peut pas être effacée.`, 'effacer-m!', id));
      break;
    case 'effacer-m!':
      try {
        await api.supprimer(`/api/missions/${id}`, { motif: valeur('#motif') });
        couche(null); mot('Mission effacée.');
        vue = 'missions'; missionOuverte = null; await rendre();
      } catch (e) { dire(direRefus(e)); }
      break;

    case 'aide': fermerMenu(); couche(coucheAide()); break;

    case 'qualifier':
      try { await api.post(`/api/profils/${id}/qualifier`); mot('Profil qualifié.'); await rendre(); }
      catch (e) { mot(direRefus(e)); }
      break;

    case 'parametre': {
      const cle = cible.dataset['cle']!;
      await enregistrerParametre(cle, Number(valeur(`#p-${cle}`)));
      break;
    }

    case 'reinit-2fa':
      couche(coucheMotif('Réinitialiser le second facteur',
        `Le compte de ${cible.dataset['nom'] ?? 'cette personne'} sera remis à zéro : mot de passe et application
         d'authentification. Un nouveau lien d'accès partira par courriel.`, 'reinit-2fa!', id));
      break;
    case 'reinit-2fa!':
      try { await api.post(`/api/comptes/${id}/second-facteur`); couche(null); mot('Lien d’accès renvoyé.'); await rendre(); }
      catch (e) { dire(direRefus(e)); }
      break;

    case 'echeances': {
      const e = await api.get<{ a_informer: unknown[]; a_purger: unknown[] }>('/api/rgpd/echeances');
      mot(`${e.a_informer.length} personne(s) à informer · ${e.a_purger.length} dossier(s) à purger.`);
      break;
    }
    case 'reversibilite':
      couche(coucheMotif('Export de réversibilité',
        `Cet export sort l'intégralité du vivier. Il est réservé au changement d'hébergeur ou à la fin de vie
         de l'outil. Les autres administrateurs en seront prévenus, et le journal en gardera la trace.`,
        'reversibilite!'));
      break;
    case 'reversibilite!':
      try {
        const donnees = await api.post('/api/rgpd/reversibilite', { motif: valeur('#motif') });
        const url = URL.createObjectURL(new Blob([JSON.stringify(donnees, null, 2)], { type: 'application/json' }));
        Object.assign(document.createElement('a'), { href: url, download: 'mana-reversibilite.json' }).click();
        URL.revokeObjectURL(url);
        couche(null); mot('Export produit et journalisé.');
      } catch (e) { dire(direRefus(e)); }
      break;

    case 'deconnexion':
      fermerMenu();
      await api.post('/api/auth/deconnexion').catch(() => {});
      location.href = '/';
      break;
  }
});

/**
 * Un formulaire ne part qu'une fois.
 *
 * Trois clics sur « Créer la mission » ont créé trois missions ; rien n'empêchait la même
 * chose ici. Le drapeau écarte les envois tant que le précédent est en vol, et le bouton
 * se grise — un bouton qui ne réagit pas invite à recliquer, ce qui est exactement le
 * geste qu'on veut éviter.
 *
 * Le bouton peut vivre hors du formulaire, relié par `form="..."` : les deux cas sont
 * cherchés, parce que l'oublier rendrait le verrou muet là où il sert le plus.
 */
const enVol = new Set<string>();
async function unSeulEnvoi(forme: HTMLFormElement, geste: () => Promise<void>): Promise<void> {
  if (enVol.has(forme.id)) return;
  enVol.add(forme.id);
  const bouton = forme.querySelector<HTMLButtonElement>('button[type="submit"]')
    ?? document.querySelector<HTMLButtonElement>(`button[form="${forme.id}"][type="submit"]`);
  const libelle = bouton?.textContent ?? null;
  if (bouton) { bouton.disabled = true; bouton.textContent = 'Enregistrement…'; }
  try { await geste(); }
  finally {
    enVol.delete(forme.id);
    // Le bouton a pu disparaître avec sa couche : le remettre en état n'a alors aucun sens.
    if (bouton?.isConnected) { bouton.disabled = false; bouton.textContent = libelle; }
  }
}

document.addEventListener('submit', async (ev) => {
  const forme = ev.target as HTMLFormElement;
  if (forme.id === 'creer') {
    ev.preventDefault();
    const fiche = recolterProfil();
    if (!fiche.prenom || !fiche.nom) return dire('Le prénom et le nom sont obligatoires.');
    await unSeulEnvoi(forme, async () => {
      try {
        const { id } = await api.post<{ id: string }>('/api/profils',
          { ...fiche, cle_creation: cleCreationProfil });
        couche(null); mot('Fiche créée — elle reste à qualifier.');
        profilOuvert = id; vue = 'profil'; await rendre();
      } catch (e) { dire(direRefus(e)); }
    });
  }
  if (forme.id === 'modifier') {
    ev.preventDefault();
    const fiche = recolterProfil();
    if (!fiche.prenom || !fiche.nom) return dire('Le prénom et le nom sont obligatoires.');
    await unSeulEnvoi(forme, async () => {
      try {
        await api.patch(`/api/profils/${profilOuvert}`, fiche);
        couche(null); mot('Fiche mise à jour.'); await rendre();
      } catch (e) { dire(direRefus(e)); }
    });
  }
  if (forme.id === 'inviter') {
    ev.preventDefault();
    try {
      await api.post('/api/comptes', {
        prenom: valeur('#i-prenom'), nom: valeur('#i-nom'),
        email: valeur('#i-email'), role: valeur('#i-role'),
      });
      couche(null); mot('Invitation envoyée.'); await rendre();
    } catch (e) { dire(direRefus(e)); }
  }
});

document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') couche(null); });

void demarrer();
export { peutEcrire };
